// Shared presentation palette for the TelcoFair screenshot-matched ERP shell.
// ThemeRuntime updates the live brand colors synchronously so React `sx`
// helpers and CSS variables always reflect Company Settings immediately.

const FALLBACK = {
  primary: '#2E6F8E',
  secondary: '#42B89B',
  accent: '#7C5CF2',
};

let runtimeBrand = { ...FALLBACK };

export const setRuntimeBrandColors = ({ primary, secondary, accent } = {}) => {
  if (primary) runtimeBrand.primary = primary;
  if (secondary) runtimeBrand.secondary = secondary;
  if (accent) runtimeBrand.accent = accent;
};

export const readCssColor = (name, fallback) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return fallback;
  const value = window.getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || fallback;
};

const normalizeHex = (value, fallback = '#000000') => {
  if (typeof value !== 'string') return fallback;
  const v = value.trim();
  if (/^#[0-9a-f]{6}$/i.test(v)) return v;
  if (/^#[0-9a-f]{3}$/i.test(v)) {
    return `#${v.slice(1).split('').map((c) => c + c).join('')}`;
  }
  return fallback;
};

const shiftHex = (value, amount) => {
  const hex = normalizeHex(value).slice(1);
  const clamp = (n) => Math.max(0, Math.min(255, n));
  const r = clamp(parseInt(hex.slice(0, 2), 16) + amount);
  const g = clamp(parseInt(hex.slice(2, 4), 16) + amount);
  const b = clamp(parseInt(hex.slice(4, 6), 16) + amount);
  return `#${[r, g, b].map((n) => n.toString(16).padStart(2, '0')).join('')}`;
};


const srgb = (channel) => {
  const v = channel / 255;
  return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
};

export const relativeLuminance = (value) => {
  const hex = normalizeHex(value, '#000000').slice(1);
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  return 0.2126 * srgb(r) + 0.7152 * srgb(g) + 0.0722 * srgb(b);
};

export const contrastRatio = (foreground, background) => {
  const l1 = relativeLuminance(foreground);
  const l2 = relativeLuminance(background);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
};

export const getContrastText = (background, dark = '#172235', light = '#FFFFFF') => {
  return contrastRatio(dark, background) >= contrastRatio(light, background) ? dark : light;
};

export const getReadableColor = (color, background = '#FFFFFF', minRatio = 4.5) => {
  let candidate = normalizeHex(color, FALLBACK.primary);
  if (contrastRatio(candidate, background) >= minRatio) return candidate;

  // Preserve the selected hue, but derive a darker UI-ink variant for labels,
  // outlines and active states when the saved company colour is very light.
  for (let amount = -12; amount >= -132; amount -= 12) {
    const shifted = shiftHex(candidate, amount);
    if (contrastRatio(shifted, background) >= minRatio) return shifted;
  }
  return '#172235';
};

export const mixHex = (a, b, ratio = 0.5) => {
  const one = normalizeHex(a, FALLBACK.primary).slice(1);
  const two = normalizeHex(b, FALLBACK.secondary).slice(1);
  const r = Math.max(0, Math.min(1, ratio));
  const channel = (start) => Math.round(
    parseInt(one.slice(start, start + 2), 16) * (1 - r) +
    parseInt(two.slice(start, start + 2), 16) * r
  );
  return `#${[channel(0), channel(2), channel(4)].map((n) => n.toString(16).padStart(2, '0')).join('')}`;
};

export const getGradientContrastText = (start, end = start, dark = '#172235', light = '#FFFFFF') => {
  const a = normalizeHex(start, FALLBACK.primary);
  const b = normalizeHex(end || start, a);
  // Use the midpoint as the practical perceived background of a two-stop
  // action gradient, then choose the foreground with the stronger contrast.
  // This keeps tenant-selected pale colours readable instead of forcing white.
  const midpoint = mixHex(a, b, 0.5);
  return getContrastText(midpoint, dark, light);
};

const primary = () => runtimeBrand.primary || readCssColor('--primary', FALLBACK.primary);
const secondary = () => runtimeBrand.secondary || readCssColor('--secondary', FALLBACK.secondary);
const accent = () => runtimeBrand.accent || readCssColor('--accent', FALLBACK.accent);

export const brandPalette = {
  // Reference screenshot: cool pearl canvas + pure white elevated surfaces.
  canvas: '#F1F6F9',
  canvasAlt: '#F7FAFC',
  canvasDeep: '#E7EFF4',
  paper: '#FFFFFF',
  paperAlt: '#F7FAFC',
  ink: '#182235',
  inkSoft: '#445168',
  inkLight: '#6D7C91',
  inkFaint: '#92A0AF',
  inkMuted: '#D4DEE6',
  inkLabel: '#334056',
  hairline: 'rgba(31,62,82,0.085)',
  hairlineSoft: 'rgba(31,62,82,0.05)',
  hairlineStrong: 'rgba(31,62,82,0.14)',
  shadow: 'rgba(25,57,78,0.12)',
  shadowLight: 'rgba(25,57,78,0.045)',
  shadowMedium: 'rgba(25,57,78,0.09)',
  shadowDark: 'rgba(25,57,78,0.16)',

  get rust() { return primary(); },
  get rustDeep() { return shiftHex(primary(), -28); },
  get rustLight() { return shiftHex(primary(), 34); },
  get rustGlow() { return shiftHex(primary(), 58); },
  get pine() { return secondary(); },
  get pineDeep() { return shiftHex(secondary(), -28); },
  get pineSoft() { return shiftHex(secondary(), 18); },
  get pineLight() { return shiftHex(secondary(), 42); },
  get pineGlow() { return shiftHex(secondary(), 60); },
  get gold() { return accent(); },
  get goldDeep() { return shiftHex(accent(), -32); },
  get goldLight() { return shiftHex(accent(), 34); },
  get amber() { return accent(); },
  get amberGlow() { return shiftHex(accent(), 48); },

  // Deliberately colourful secondary accents used for stat/module/company cards.
  blue: '#2F6FED',
  sky: '#13A7D8',
  indigo: '#5B58D6',
  indigoGlow: '#918CF2',
  purple: '#8B4DE8',
  purpleLight: '#B486FF',
  plum: '#C047A8',
  plumGlow: '#E789D3',
  teal: '#0F9E9A',
  tealDeep: '#087570',
  tealLight: '#54CDC6',
  tealGlow: '#9DE6E1',
  green: '#13A773',
  greenDeep: '#0A7B55',
  mint: '#28BE91',
  rose: '#E84B7A',
  coral: '#F06A5F',
  orange: '#F39A24',
  clay: '#D87C39',
  clayLight: '#F2AE6D',
  ember: '#DC3D55',
  navy: '#26415C',
  slate: '#62758B',

  success: '#16A36A',
  warning: '#E29A20',
  danger: '#D94B5A',
  info: '#2B7FB7',
  admin: '#7C5CF2',
  linkedin: '#0A66C2',
  whatsapp: '#16A36A',
};
